'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

// Backend integration point: replace with /api/agents/:id
const agent = {
  name: 'Khalid Al-Rashid',
  title: 'Senior Property Consultant',
  agency: 'OROW Premium Real Estate LLC',
  phone: '+971 50 234 5678',
  whatsapp: '+971 50 234 5678',
  email: 'khalid.alrashid@orow.ae',
  license: 'RERA-AGT-2023-14407',
  rating: 4.9,
  reviews: 142,
  deals: 284,
  yearsExp: 9,
  languages: ['English', 'Arabic', 'Russian'],
  specialties: ['Luxury Villas', 'Beachfront Properties', 'Investment Properties'],
};

interface InquiryForm {
  name: string;
  phone: string;
  email: string;
  message: string;
}

export default function AgentCard() {
  const [showForm, setShowForm] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<InquiryForm>();

  const onSubmit = async (data: InquiryForm) => {
    // Backend integration point: POST /api/inquiries
    await new Promise(r => setTimeout(r, 800));
    toast.success('Inquiry sent to Khalid Al-Rashid. You\'ll hear back within 2 hours.');
    reset();
    setShowForm(false);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-card sticky top-24">
      {/* Agent Info */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon name="UserIcon" size={28} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-foreground text-base">{agent.name}</div>
          <div className="text-muted-foreground text-xs">{agent.title}</div>
          <div className="text-primary text-xs font-semibold mt-0.5">{agent.agency}</div>
          <div className="flex items-center gap-1 mt-1">
            <Icon name="StarIcon" size={13} className="text-accent" />
            <span className="text-xs font-bold text-foreground">{agent.rating}</span>
            <span className="text-xs text-muted-foreground">({agent.reviews} reviews)</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-border">
        {[
          { label: 'Deals', value: agent.deals },
          { label: 'Years Exp', value: agent.yearsExp },
          { label: 'Reviews', value: agent.reviews },
        ].map((s) => (
          <div key={`agent-stat-${s.label}`} className="text-center bg-muted rounded-xl p-2">
            <div className="font-bold text-foreground text-base font-tabular">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Languages */}
      <div className="mb-4">
        <div className="text-xs text-muted-foreground mb-1.5">Languages</div>
        <div className="flex gap-1.5 flex-wrap">
          {agent.languages.map((l) => (
            <span key={`lang-${l}`} className="text-xs bg-muted text-foreground/70 px-2 py-1 rounded-full border border-border">{l}</span>
          ))}
        </div>
      </div>

      {/* License */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4 pb-4 border-b border-border">
        <Icon name="IdentificationIcon" size={13} className="text-primary" />
        RERA License: {agent.license}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 mb-4">
        <a
          href={`tel:${agent.phone}`}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-all active:scale-95"
        >
          <Icon name="PhoneIcon" size={16} />
          Call Agent
        </a>
        <a
          href={`https://wa.me/${agent.whatsapp.replace(/\s+/g, '')}`}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition-all active:scale-95"
        >
          <Icon name="ChatBubbleLeftEllipsisIcon" size={16} />
          WhatsApp
        </a>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-primary text-primary font-semibold text-sm hover:bg-primary hover:text-white transition-all active:scale-95"
        >
          <Icon name="EnvelopeIcon" size={16} />
          Send Inquiry
        </button>
      </div>

      {/* Inquiry Form */}
      {showForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 pt-4 border-t border-border animate-slide-up">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Your Name *</label>
            <input
              {...register('name', { required: 'Name is required' })}
              className="input-field text-xs py-2"
              placeholder="Full name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Phone *</label>
            <input
              {...register('phone', { required: 'Phone is required' })}
              className="input-field text-xs py-2"
              placeholder="+971 50 000 0000"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Email</label>
            <input
              {...register('email')}
              type="email"
              className="input-field text-xs py-2"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Message</label>
            <textarea
              {...register('message')}
              rows={3}
              className="input-field text-xs py-2 resize-none"
              defaultValue="I'm interested in this property. Please contact me to arrange a viewing."
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary-dark transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
                Sending...
              </>
            ) : 'Send Inquiry'}
          </button>
        </form>
      )}

      {/* Schedule Viewing */}
      <button className="w-full mt-2 py-2.5 rounded-xl bg-accent/20 text-accent-dark border border-accent/30 font-semibold text-sm hover:bg-accent/30 transition-all flex items-center justify-center gap-2">
        <Icon name="CalendarDaysIcon" size={16} />
        Schedule Viewing
      </button>
    </div>
  );
}